import React, { useEffect, useState } from 'react';
import { Box, VStack, HStack, Text, Badge, Circle, Spinner, Divider } from '@chakra-ui/react';
import {
  obtenerLineaTiempo, LineaTiempoEvento,
} from '../../../api/immApi';
import {
  fmt, LINEA_TIEMPO_LABEL, LINEA_TIEMPO_COLOR, IMM_COLOR, EstatusBadge,
} from '../_shared';

interface Props {
  expedienteId: number;
  /** Se recarga cuando cambia esta llave (p. ej. tras registrar una atención). */
  refreshKey?: number;
}

/**
 * Línea de tiempo cronológica del expediente. Consume
 * GET /imm/expedientes/:id/linea-tiempo, que agrega atenciones, casos,
 * canalizaciones, decisiones, órdenes, citas y constancias ya ordenadas por fecha.
 */
const LineaTiempo: React.FC<Props> = ({ expedienteId, refreshKey }) => {
  const [eventos, setEventos] = useState<LineaTiempoEvento[]>([]);
  const [cargando, setCargando] = useState(false);

  useEffect(() => {
    let vivo = true;
    setCargando(true);
    obtenerLineaTiempo(expedienteId)
      .then((data) => { if (vivo) setEventos(data || []); })
      .catch(() => { if (vivo) setEventos([]); })
      .finally(() => { if (vivo) setCargando(false); });
    return () => { vivo = false; };
  }, [expedienteId, refreshKey]);

  if (cargando) {
    return (
      <HStack py={6} justify="center" color="gray.500">
        <Spinner size="sm" color={IMM_COLOR} />
        <Text fontSize="sm">Cargando línea de tiempo...</Text>
      </HStack>
    );
  }

  if (eventos.length === 0) {
    return (
      <Box py={8} textAlign="center">
        <Text color="gray.400" fontSize="sm">
          Sin eventos registrados. Las atenciones, casos, canalizaciones, decisiones,
          órdenes, citas y constancias aparecerán aquí cronológicamente.
        </Text>
      </Box>
    );
  }

  return (
    <VStack align="stretch" spacing={0} pl={2}>
      {eventos.map((ev, idx) => {
        const color = LINEA_TIEMPO_COLOR[ev.tipo] || 'gray';
        const esUltimo = idx === eventos.length - 1;
        return (
          <HStack key={`${ev.tipo}-${ev.id}`} align="stretch" spacing={3}>
            {/* Rail con punto y línea */}
            <VStack spacing={0} minW="24px">
              <Circle size="14px" bg={`${color}.500`} mt={1} border="2px solid white" boxShadow="0 0 0 2px var(--chakra-colors-gray-200)" />
              {!esUltimo && <Box flex="1" w="2px" bg="gray.200" />}
            </VStack>
            {/* Contenido */}
            <Box pb={esUltimo ? 0 : 5} flex="1">
              <HStack flexWrap="wrap" spacing={2} mb={0.5}>
                <Badge colorScheme={color} fontSize="2xs" textTransform="uppercase">
                  {LINEA_TIEMPO_LABEL[ev.tipo] || ev.tipo}
                </Badge>
                <Text fontSize="xs" color="gray.500">{fmt(ev.fecha)}</Text>
                {ev.estatus && <EstatusBadge value={ev.estatus} />}
              </HStack>
              <Text fontSize="sm" fontWeight="medium">{ev.titulo}</Text>
            </Box>
          </HStack>
        );
      })}
      <Divider mt={2} display="none" />
    </VStack>
  );
};

export default LineaTiempo;
